import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { toast } from "@/lib/notify";
import { User, Building2, Mail, CheckCircle2, Clock, FileText, Tag, Upload, Download, Eye, RefreshCw } from "lucide-react";
import { filterByInvestorType } from "@/lib/investorTypes";

const ONBOARDING_STYLES = {
  pending: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  in_progress: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  submitted: "bg-violet-500/10 text-violet-400 border-violet-500/20",
  complete: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
};

export default function InvestorProfile() {
  const { user } = useAuth();
  const [investor, setInvestor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({});
  const [replacing, setReplacing] = useState({});
  const fileRefs = useRef({});
  const replaceRefs = useRef({});

  const loadProfile = useCallback(async () => {
    try {
      const { data: investors } = await API.get("/investors");
      if (investors.length > 0) {
        const { data } = await API.get(`/investors/${investors[0].id}`);
        setInvestor(data);
      }
    } catch {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const handleUpload = async (docId, files) => {
    if (!investor || !files?.length) return;
    setUploading(prev => ({ ...prev, [docId]: true }));
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        await API.post(`/investors/${investor.id}/documents/${docId}/investor-upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success("Document uploaded successfully");
      loadProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(prev => ({ ...prev, [docId]: false }));
    }
  };

  const handleReplace = async (docId, fileId, file) => {
    if (!investor || !file) return;
    const key = `${docId}_${fileId}`;
    setReplacing(prev => ({ ...prev, [key]: true }));
    try {
      const formData = new FormData();
      formData.append("file", file);
      await API.put(`/investors/${investor.id}/documents/${docId}/investor-replace/${fileId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document replaced successfully");
      loadProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Replace failed");
    } finally {
      setReplacing(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleDownload = async (docId, fileName, fileId) => {
    if (!investor) return;
    try {
      const params = fileId ? `?file_id=${fileId}` : "";
      const res = await API.get(`/investors/${investor.id}/documents/${docId}/download${params}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || "document";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const handlePreview = async (docId, fileId) => {
    if (!investor) return;
    try {
      const params = fileId ? `?preview=1&file_id=${fileId}` : "?preview=1";
      const res = await API.get(`/investors/${investor.id}/documents/${docId}/download${params}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] || "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch {
      toast.error("Preview not available");
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  if (!investor) return (
    <div className="flex flex-col items-center justify-center h-64 space-y-3" data-testid="no-profile">
      <User className="w-12 h-12 text-muted-foreground" strokeWidth={1} />
      <p className="text-muted-foreground">No investor profile found</p>
    </div>
  );

  const fund = investor.fund || {};
  const personalInfo = investor.personal_info || {};
  const distEmails = investor.distribution_emails || [];
  const documents = investor.documents || [];
  const investorType = personalInfo["Investor Type"] || personalInfo["Entity Type"] || "";
  const fields = filterByInvestorType(fund.onboarding_workflow?.required_fields || [], investorType);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="investor-profile-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">My Profile</h1>
        <p className="text-muted-foreground text-sm mt-1">Your investment details and onboarding status</p>
      </div>

      {/* Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="stat-card bg-blue-500/10 border-blue-500/20">
          <CardContent className="p-5">
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Fund</span>
            <p className="font-heading text-lg font-bold mt-1 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-blue-500 dark:text-blue-400" /> {fund.name || "—"}
            </p>
          </CardContent>
        </Card>
        <Card className="stat-card bg-emerald-500/10 border-emerald-500/20">
          <CardContent className="p-5">
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Onboarding</span>
            <div className="mt-1">
              <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${ONBOARDING_STYLES[investor.onboarding_status] || ""}`}>
                {investor.onboarding_status?.replace("_", " ")}
              </Badge>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card bg-violet-500/10 border-violet-500/20">
          <CardContent className="p-5">
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Documents</span>
            <p className="font-heading text-lg font-bold mt-1">
              {documents.filter(d => d.status === "uploaded").length}/{documents.length} uploaded
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Personal Info */}
      <Card data-testid="personal-info-card">
        <CardHeader className="p-6 pb-3">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <User className="w-5 h-5 text-primary" /> Personal Information
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-3 rounded-lg bg-secondary/30 border border-border/50">
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Name</span>
              <p className="text-sm font-medium mt-1">{investor.name}</p>
            </div>
            <div className="p-3 rounded-lg bg-secondary/30 border border-border/50">
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Email</span>
              <p className="text-sm font-medium mt-1">{investor.email}</p>
            </div>
            {fields.map((field) => (
              <div key={field.id || field.field_name} className="p-3 rounded-lg bg-secondary/30 border border-border/50">
                <span className="text-xs text-muted-foreground uppercase tracking-wider">{field.field_name}</span>
                <p className="text-sm font-medium mt-1">{personalInfo[field.field_name] || "—"}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Distribution Emails */}
      {distEmails.length > 0 && (
        <Card data-testid="dist-emails-card">
          <CardHeader className="p-6 pb-3">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" /> Distribution List
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0 space-y-2">
            {distEmails.map((de, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 border border-border/50">
                <span className="text-sm">{de.email}</span>
                <Badge variant="secondary" className="text-[10px]">
                  <Tag className="w-3 h-3 mr-1" /> {de.label}
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Documents */}
      <Card data-testid="documents-card">
        <CardHeader className="p-6 pb-3">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" /> Documents
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 pt-0 space-y-3">
          {documents.map((doc) => {
            const slotFiles = (doc.files && doc.files.length > 0)
              ? doc.files
              : (doc.stored_name ? [{ file_id: "legacy", file_name: doc.file_name, stored_name: doc.stored_name, uploaded_at: doc.uploaded_at }] : []);
            return (
              <div key={doc.doc_id} className="rounded-lg bg-secondary/30 border border-border/50 p-4 space-y-3" data-testid={`profile-doc-${doc.doc_id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${doc.status === "uploaded" ? "bg-emerald-500/10" : "bg-amber-500/10"}`}>
                      {doc.status === "uploaded" ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Clock className="w-4 h-4 text-amber-400" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{doc.doc_name}</p>
                      {slotFiles.length > 0 && (
                        <p className="text-[10px] text-muted-foreground/70">{slotFiles.length} file{slotFiles.length === 1 ? "" : "s"} uploaded</p>
                      )}
                    </div>
                    {doc.required && <Badge variant="outline" className="text-[10px] text-amber-400 border-amber-500/20 ml-2">Required</Badge>}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      className="hidden"
                      ref={(el) => { fileRefs.current[doc.doc_id] = el; }}
                      onChange={(e) => {
                        if (e.target.files?.length) handleUpload(doc.doc_id, Array.from(e.target.files));
                        e.target.value = "";
                      }}
                      data-testid={`upload-input-${doc.doc_id}`}
                    />
                    {slotFiles.length === 0 && (
                      <Button
                        variant="secondary"
                        size="sm"
                        className="border border-border gap-1.5 text-xs"
                        onClick={() => fileRefs.current[doc.doc_id]?.click()}
                        disabled={uploading[doc.doc_id]}
                        data-testid={`upload-btn-${doc.doc_id}`}
                      >
                        <Upload className="w-3.5 h-3.5" />
                        {uploading[doc.doc_id] ? "Uploading..." : "Upload"}
                      </Button>
                    )}
                  </div>
                </div>
                {slotFiles.length > 0 && (
                  <div className="space-y-1.5 pl-11" data-testid={`profile-doc-files-${doc.doc_id}`}>
                    {slotFiles.map((f) => {
                      const replaceKey = `${doc.doc_id}_${f.file_id}`;
                      return (
                        <div key={f.file_id || f.stored_name} className="flex items-center justify-between gap-2 text-xs bg-card/50 border border-border/50 rounded-md px-3 py-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                            <span className="truncate" title={f.file_name}>{f.file_name || "(unnamed)"}</span>
                            {f.uploaded_at && (
                              <span className="text-muted-foreground/60 flex-shrink-0">· {new Date(f.uploaded_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button variant="ghost" size="icon" className="h-7 w-7" title="Preview" onClick={() => handlePreview(doc.doc_id, f.file_id)} data-testid={`preview-btn-${f.file_id}`}>
                              <Eye className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" title="Download" onClick={() => handleDownload(doc.doc_id, f.file_name, f.file_id)} data-testid={`download-btn-${f.file_id}`}>
                              <Download className="w-3.5 h-3.5" />
                            </Button>
                            {f.file_id && f.file_id !== "legacy" && (
                              <>
                                <input
                                  type="file"
                                  className="hidden"
                                  ref={(el) => { replaceRefs.current[replaceKey] = el; }}
                                  onChange={(e) => {
                                    if (e.target.files?.[0]) handleReplace(doc.doc_id, f.file_id, e.target.files[0]);
                                    e.target.value = "";
                                  }}
                                  data-testid={`replace-input-${f.file_id}`}
                                />
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  title="Replace"
                                  onClick={() => replaceRefs.current[replaceKey]?.click()}
                                  disabled={replacing[replaceKey]}
                                  data-testid={`replace-btn-${f.file_id}`}
                                >
                                  <RefreshCw className={`w-3.5 h-3.5 ${replacing[replaceKey] ? "animate-spin" : ""}`} />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {documents.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No documents</p>}
        </CardContent>
      </Card>
    </div>
  );
}
